import PageLoadingSpinner from "@/Shared/Components/CustomComponents/PageLoadingSpinner";
import ShipmentNotFound from "@/Shared/Shipment/CustomComponents/ShipmentNotFound";
import { LoadFailed } from "@/Shared/Components/LoadFailed";
import { isNotFoundError } from "@/Helpers/isNotFoundError";
import { useCoupons } from "@/Shared/Coupons/Hooks/useCoupons";
import useInterval from "@/Hooks/useInterval";
import { useOnce } from "@/Hooks/useOnce";
import useQuery from "@/Hooks/useQuery";
import { useShipmentService } from "@/Services/ShipmentService";
import { useFreightClaimsApi } from "@/Hooks/useFreightClaimsApi";
import type { FreightClaim } from "@freightsimple/generated-dashboard-openapi-client/models/FreightClaim";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import { DocumentType } from "@freightsimple/generated-dashboard-openapi-client/models/DocumentType";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ImageSectionItem } from "./CustomComponents/ViewShipmentsImageSection";
import type { DocumentsSectionItem } from "@/Shared/Shipment/CustomComponents/ShipmentDocumentsSectionGeneric";
import {
  viewShipmentsIsImageDocument,
  viewShipmentsToDocumentItem,
  viewShipmentsWithPlaceholderFor,
} from "./Helpers/viewShipmentsDocumentItems";
import { ViewShipmentsAfterBooking } from "./Internal/viewShipmentsAfterBooking";

function ViewShipmentScreen() {
  const query = useQuery();
  const navigate = useNavigate();

  const shipmentsService = useShipmentService();
  const freightClaimsApi = useFreightClaimsApi();

  // Query params can parse as arrays; only a single string value is a usable
  // shipment id. A missing id falls through to the not-found handling below.
  const shipmentIdQueryValue = query["shipmentId"];
  const shipmentId =
    typeof shipmentIdQueryValue === "string" ? shipmentIdQueryValue : "";

  const [shipment, setShipment] = useState<Shipment | undefined>();
  const [shipmentNotFound, setShipmentNotFound] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [documentItems, setDocumentItems] = useState<
    Array<DocumentsSectionItem>
  >([]);
  const [imageItems, setImageItems] = useState<Array<ImageSectionItem>>([]);
  const [freightClaims, setFreightClaims] = useState<FreightClaim[]>([]);
  const { getDocuments, downloadDocument } = useShipmentService();

  const { refreshCoupon } = useCoupons();

  // Need to make sure we clear the coupon ui after booking is complete
  // since booking is highly async - this is the easiest way
  useOnce(function () {
    refreshCoupon();
  });

  async function loadShipment() {
    try {
      const response = await shipmentsService.getShipment(shipmentId);
      setLoadFailed(false);

      // If shipment is not booked, redirect to quotes page
      if (!response.booked) {
        navigate(`/book/quotes?shipmentId=${shipmentId}`);
        return;
      }

      if (!response.shipment) {
        setShipmentNotFound(true);
        return;
      }

      setShipment(response.shipment);

      const allDocuments = (
        await getDocuments(response.shipment.shipmentId)
      ).map((d) =>
        viewShipmentsToDocumentItem(d, () =>
          downloadDocument(d.documentId, d.documentType),
        ),
      );

      const claims = await freightClaimsApi.getByShipmentId({
        shipmentId,
      });

      setFreightClaims(claims);

      const documents = allDocuments.filter(
        (doc) => !viewShipmentsIsImageDocument(doc.documentType),
      );
      const images = allDocuments.filter((doc) =>
        viewShipmentsIsImageDocument(doc.documentType),
      );

      setDocumentItems(
        viewShipmentsWithPlaceholderFor(
          documents,
          DocumentType.ProofOfDelivery,
        ),
      );
      setImageItems(images);
    } catch (e) {
      if (isNotFoundError(e)) {
        setShipmentNotFound(true);
      } else {
        console.warn("Error loading shipment:", { e });
        setLoadFailed(true);
      }
    }
  }

  function retryLoad() {
    setLoadFailed(false);
    loadShipment();
  }

  // Refresh the page every two minutes. A refresh that fails keeps showing
  // the shipment already on screen; only a first load that fails shows the
  // failed-load state.
  useInterval(
    function () {
      loadShipment();
    },
    2 * 60 * 1000,
  );

  useOnce(async () => {
    loadShipment();
  });

  if (shipmentNotFound) {
    return <ShipmentNotFound />;
  }
  if (!shipment) {
    return loadFailed ? (
      <LoadFailed onRetry={retryLoad} />
    ) : (
      <PageLoadingSpinner />
    );
  }

  return (
    <ViewShipmentsAfterBooking
      freightClaims={freightClaims}
      shipment={shipment}
      onReloadShipment={loadShipment}
      documentItems={documentItems}
      imageItems={imageItems}
    />
  );
}
export default ViewShipmentScreen;
